import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { FormAPI, InputAPI, RadioGroupAPI, SelectableItem, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Inject } from '@odinjs/odin';
import moment from 'moment';

import BaseController from '../../../BaseController';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { ScoreMorseResponse } from '../../score-types';
import { SimpleDialog, } from '../../../dialog-helper';
import { DialogType, ModalType, SimpleDialogParams } from '../../../../interfaces/simple-dialog';
import DialogService from '../../../../services/dialog-service';
import { FragmentNames, ScoreMorseCreateAliases } from '../../../../codegen/index';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MorseRadioGroupExpressions } from '../radio-group-data/morse-radio-group-expressions';

@Controller({ component: ScoreMorseCreateAliases.SCORE_MORSE_ADD_LAYOUT, fragment: FragmentNames.SCORE_MORSE_CREATE })
export default class ScoreMorseCreateController extends BaseController {

  @Inject(ScoreMorseCreateAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreMorseCreateAliases.FORM_SCORE_MORSE_CREATE)
  private formScoreMorseCreateAPI!: FormAPI;

  @OnMount(ScoreMorseCreateAliases.RADIO_GROUP_AMBULATORY_AID)
  handleMountRadioGroupAmbulatoryAID(event: MountEvent): void {
    const radioGroupApi = event.target as RadioGroupAPI;
    const radioGroupHelpDeambular= 'helpDeambular';
    this.scaleRadioGroup(614296, radioGroupApi, radioGroupHelpDeambular)
  }

  @OnMount(ScoreMorseCreateAliases.RADIO_GROUP_GAIT_CHARACTERISTIC)
  handleMountRadioGroupGaitCharacteristic(event: MountEvent): void {
    const radioGroupWalkingMode= 'walkingMode';
    const radioGroupApi = event.target as RadioGroupAPI;
    this.scaleRadioGroup(614300, radioGroupApi, radioGroupWalkingMode)
  }

  @OnMount(ScoreMorseCreateAliases.RADIO_GROUP_MENTAL_STATE)
  handleMountRadioGroupMentalStateD(event: MountEvent): void {
    const radioGroupMentalState= 'mentalState';
    const radioGroupApi = event.target as RadioGroupAPI;
    this.scaleRadioGroup(614298, radioGroupApi, radioGroupMentalState)
  }

  @OnMount({ target: ScoreMorseCreateAliases.FORM_SCORE_MORSE_CREATE })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_MORSE
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.formScoreMorseCreateAPI.setLoading(true)
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
    timeService.getCurrentDateTime()
      .then(date => {
        this.context.set({ initialDate: date });
        this.setupDateInputApi.setValue(date)
      })
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
      .finally(() => {
        this.formScoreMorseCreateAPI.setLoading(false);
      });
  }

  @OnBeforeSubmit({ target: ScoreMorseCreateAliases.FORM_SCORE_MORSE_CREATE })
  handleBeforeSubmit(): void {
    const formData = this.formScoreMorseCreateAPI.getRecord();
    const { encounterId, idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const dateEvaluation = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT)
    const helpDeambular = formData.helpDeambular as SelectableItem;
    const mentalState = formData.mentalState as SelectableItem;
    const walkingMode = formData.walkingMode as SelectableItem;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const sequenceNumberMorse = Number.isNaN(parseInt(idScoreIndexes as string)) ? null : idScoreIndexes as number;

    const body = {
      dateEvaluation,
      diaSecondary: BooleanUtils.isPositiveValue(formData.diaSecondary as boolean),
      helpDeambular: helpDeambular.value,
      heparin: BooleanUtils.isPositiveValue(formData.heparin as boolean),
      histFall: BooleanUtils.isPositiveValue(formData.histFall as boolean),
      idScoreIndexes: sequenceNumberMorse,
      mentalState: mentalState.value,
      nrAttendance: encounterId as number,
      professional: user.physicalPersonId,
      walkingMode: walkingMode.value,
    }

    this.createNewMorseScore(body);
  }

  private scaleRadioGroup(expressionId: number, radioGroup: RadioGroupAPI, type: string): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        const initialDate = this.context.get('initialDate');
        const scoreMorse = this.context.get('scoreMorse') as ScoreMorseResponse;
        if (!scoreMorse) {
          radioGroup.setValue(responseBody[0]);
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            dateEvaluation: initialDate,
            [type]:responseBody[0]
          });
          return
        }

        if (type === MorseRadioGroupExpressions.HELP_DEAMBULAR) {
          const helpDeambular = responseBody.filter(({ key }) => scoreMorse?.helpDeambular === Number(key))[0]
          radioGroup.setValue(helpDeambular)
          this.context.set({ scoreMorse: {
            ...scoreMorse,
            helpDeambular
          }})
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            helpDeambular
          })
        }

        if (type === MorseRadioGroupExpressions.MENTAL_STATE) {
          const mentalState = responseBody.filter(({ key }) => scoreMorse?.mentalState === Number(key))[0]
          radioGroup.setValue(mentalState)
          this.context.set({ scoreMorse: {
            ...scoreMorse,
            mentalState
          }})
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            mentalState
          })
        }

        if (type === MorseRadioGroupExpressions.WALKING_MODE) {
          const walkingMode = responseBody.filter(({ key }) => scoreMorse?.walkingMode === Number(key))[0]
          radioGroup.setValue(walkingMode)
          this.context.set({ scoreMorse: {
            ...scoreMorse,
            walkingMode
          }})
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            walkingMode
          })
        }
      }).catch((error) => {
        this.showToast(error?.body.errorMessage, ToastLevel.ERROR);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private releaseMorseScore(sequenceNumber: number): void {
    this.formScoreMorseCreateAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_SCORE_MORSE, { sequenceNumber })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formScoreMorseCreateAPI.setLoading(false);
      });
  }

  private showModalSubmit(idScoreIndexes: number): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.release-title-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseMorseScore(idScoreIndexes) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  private createNewMorseScore(body: Record<string, Value>): void {
    this.formScoreMorseCreateAPI.setLoading(true);
    HttpService.post(Endpoint.POST_CREATE_SCORE_MORSE, undefined, { body })
      .then((response) => {
        const {idScoreIndexes} = response.body as ScoreMorseResponse;
        this.showModalSubmit(idScoreIndexes as number);
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
      .finally(() => this.formScoreMorseCreateAPI.setLoading(false));
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => { this.goBack() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnUnmount(ScoreMorseCreateAliases.SCORE_MORSE_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_MORSE;
  }

  @OnBeforeCancel(ScoreMorseCreateAliases.FORM_SCORE_MORSE_CREATE)
  handleCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }
}
